<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import SectionLayout from './SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Avatar from 'next/avatar/Avatar.vue';

const { t } = useI18n();
const store = useStore();
const { currentAccount, updateAccount } = useAccount();

const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');

const selectedUserIds = ref([]);
const fallbackTeamId = ref(null);
const isSubmitting = ref(false);

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
});

watch(
  currentAccount,
  () => {
    const settings = currentAccount.value?.settings || {};
    selectedUserIds.value = (settings.chatbot_folder_visible_user_ids || [])
      .map(id => Number(id))
      .filter(Boolean);
    fallbackTeamId.value = settings.bot_failure_fallback_team_id
      ? Number(settings.bot_failure_fallback_team_id)
      : null;
  },
  { deep: true, immediate: true }
);

const agentList = computed(() => agents.value || []);
const teamOptions = computed(() => teams.value || []);

const isUserSelected = userId => selectedUserIds.value.includes(Number(userId));

const toggleUser = userId => {
  const id = Number(userId);
  if (isUserSelected(id)) {
    selectedUserIds.value = selectedUserIds.value.filter(item => item !== id);
  } else {
    selectedUserIds.value = [...selectedUserIds.value, id];
  }
};

const saveSettings = async () => {
  isSubmitting.value = true;
  try {
    await updateAccount({
      chatbot_folder_visible_user_ids: selectedUserIds.value,
      bot_failure_fallback_team_id: fallbackTeamId.value || null,
    });
    useAlert(t('GENERAL_SETTINGS.FORM.CHATBOT.API.SUCCESS'));
  } catch (_error) {
    useAlert(t('GENERAL_SETTINGS.FORM.CHATBOT.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.CHATBOT.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.CHATBOT.NOTE')"
    with-border
  >
    <div class="grid gap-5">
      <WithLabel
        name="bot-failure-team"
        :label="t('GENERAL_SETTINGS.FORM.CHATBOT.FALLBACK_TEAM.LABEL')"
      >
        <select v-model="fallbackTeamId" class="!mb-0 text-sm w-full">
          <option :value="null">
            {{ t('GENERAL_SETTINGS.FORM.CHATBOT.FALLBACK_TEAM.PLACEHOLDER') }}
          </option>
          <option v-for="team in teamOptions" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
        <template #help>
          {{ t('GENERAL_SETTINGS.FORM.CHATBOT.FALLBACK_TEAM.HELP') }}
        </template>
      </WithLabel>

      <WithLabel
        name="chatbot-folder-users"
        :label="t('GENERAL_SETTINGS.FORM.CHATBOT.VISIBLE_USERS.LABEL')"
      >
        <div
          class="max-h-64 overflow-y-auto border border-n-weak rounded-lg divide-y divide-n-weak"
        >
          <label
            v-for="agent in agentList"
            :key="agent.id"
            class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-n-alpha-2"
          >
            <Checkbox
              :model-value="isUserSelected(agent.id)"
              @update:model-value="toggleUser(agent.id)"
            />
            <Avatar
              :src="agent.thumbnail"
              :name="agent.name"
              :size="24"
              rounded-full
            />
            <span class="text-sm text-n-slate-12 truncate">
              {{ agent.name }}
              <span class="text-n-slate-10">· {{ agent.role }}</span>
            </span>
          </label>
          <p v-if="!agentList.length" class="px-3 py-4 text-sm text-n-slate-11">
            {{ t('GENERAL_SETTINGS.FORM.CHATBOT.VISIBLE_USERS.EMPTY') }}
          </p>
        </div>
        <template #help>
          {{ t('GENERAL_SETTINGS.FORM.CHATBOT.VISIBLE_USERS.HELP') }}
        </template>
      </WithLabel>

      <div>
        <NextButton
          blue
          :is-loading="isSubmitting"
          type="button"
          @click="saveSettings"
        >
          {{ t('GENERAL_SETTINGS.FORM.CHATBOT.UPDATE_BUTTON') }}
        </NextButton>
      </div>
    </div>
  </SectionLayout>
</template>
